import {
  AppointmentProduct,
  AppointmentType,
  Clinic,
  Membership,
  StripeTokenResponse,
  themes,
  TimeSlot,
  ValidPromo
} from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import React, { useState } from 'react';
import {
  CardCVCElement,
  CardElement,
  CardExpiryElement,
  CardNumberElement,
  injectStripe,
  ReactStripeElements
} from 'react-stripe-elements';
import styled from 'styled-components';
import Media from 'react-media';

import Loader from '../../../components/Loader';
import { stripeInputStyle } from '../../../components/TextInput/NewTextInput';
import usePromo from '../../../hooks/usePromo';
import { showErrorToast, showSuccessToast } from '../../../utilities/notificationUtils';
import PromoCodeBase from '../steps/PromoCode';
import { PaymentFormWrapper } from '../steps/styles';
import ConfirmDetails from './ConfirmDetails';
import TextInput from '../../../components/TextInput/NewTextInput';
const { breakpoints } = themes;

interface WidthProps {
  width?: number;
}

interface Props {
  appointment: AppointmentType | Membership;
  clinic: Clinic | null;
  loading?: boolean;
  noPayment?: () => void;
  onPaymentTermsChecked: (checked: boolean) => void;
  onSubmit: (token: StripeTokenResponse) => void;
  onValidPromoChange: (validPromo: ValidPromo | null) => void;
  paymentTermsChecked?: boolean;
  product: AppointmentProduct<AppointmentType>;
  stripe?: ReactStripeElements.StripeProps;
  timeSlot: TimeSlot | null;
  hasEmployer?: boolean;
  validPromo: ValidPromo | null;
}

const PaymentForm = (props: Props) => {
  const {
    appointment,
    clinic,
    noPayment,
    onPaymentTermsChecked,
    onSubmit,
    onValidPromoChange,
    paymentTermsChecked,
    product,
    stripe,
    timeSlot,
    loading = false,
    hasEmployer = false,
    validPromo
  } = props;
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState(false);
  const [zipCode, setZipCode] = useState({ value: '', fresh: true, error: false, complete: false });
  const { validatePromo } = usePromo(product);

  const updateZipCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = RegExp('[0-9]{5}');
    const isValid = regex.test(event.currentTarget.value);
    setZipCode({
      value: event.currentTarget.value,
      fresh: false,
      error: !isValid,
      complete: event.currentTarget.value ? event.currentTarget.value.trim().length === 5 : false
    });
  };

  if (!stripe) {
    return (
      <LoaderContainer>
        <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
      </LoaderContainer>
    );
  }

  return (
    <PaymentFormWrapper>
      <form
        onSubmit={async e => {
          e.preventDefault();
          if (noPayment) return noPayment();
          const isMediaUpToMedium = window.innerWidth <= breakpoints.raw.md;

          try {
            if (!stripe) throw new Error("We're sorry, something went wrong.");
            // eslint-disable-next-line @typescript-eslint/camelcase
            const options = isMediaUpToMedium ? { address_zip: zipCode.value } : undefined;
            // @ts-ignore
            const token: StripeTokenResponse = await stripe.createToken(options);
            onSubmit(token);
          } catch (err) {
            Honeybadger.notify(err, 'UnhandledStripeError');
            showErrorToast(
              typeof err === 'string'
                ? err
                : "We're sorry something unexpected happened. Please try again."
            );
          }
        }}
      >
        {(!noPayment || hasEmployer) && (
          <PaymentFormContainer>
            {!hasEmployer && (
              <Media query={{ maxWidth: breakpoints.raw.md }}>
                {upToMedium =>
                  upToMedium ? (
                    <>
                      <CardElementContainer id="stripe-cardnumber">
                        <CardNumberElement style={stripeInputStyle} />
                      </CardElementContainer>
                      <SmallMediaCardElementsContainer>
                        <CardElementContainer id="stripe-exp-date">
                          <CardExpiryElement style={stripeInputStyle} />
                        </CardElementContainer>
                        <CardElementContainer id="stripe-cvc">
                          <CardCVCElement style={stripeInputStyle} />
                        </CardElementContainer>
                      </SmallMediaCardElementsContainer>
                      <CardElementContainer
                        id="zip-code"
                        className={`${zipCode.complete ? 'complete' : ''} ${
                          zipCode.error ? 'error' : ''
                        }`}
                      >
                        <TextInput
                          name="zip"
                          inputType="zip"
                          placeholder="55555"
                          value={zipCode.value}
                          onChange={updateZipCode}
                          error={zipCode.error}
                          autoComplete="postal-code"
                        />
                      </CardElementContainer>
                    </>
                  ) : (
                    <CardElementContainer>
                      <CardElement style={stripeInputStyle} />
                    </CardElementContainer>
                  )
                }
              </Media>
            )}
            <PromoCode
              value={promoCode || ''}
              error={promoError !== null}
              hasEmployer={hasEmployer}
              onChange={event => {
                setPromoCode(event.target.value);
              }}
              onSubmit={() => {
                setPromoError(false);
                validatePromo(promoCode)
                  .then(({ validPromo, error }) => {
                    onValidPromoChange(validPromo || null);
                    if (error) {
                      setPromoError(true);
                      if (window.analytics) {
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        window.analytics.track('Promo Code Failed', { promo_code: promoCode });
                      }
                      return showErrorToast(error);
                    }
                    showSuccessToast('Promo code accepted.');
                    if (window.analytics) {
                      // eslint-disable-next-line @typescript-eslint/camelcase
                      window.analytics.track('Promo Code Succeeded', { promo_code: promoCode });
                    }
                  })
                  .catch(error => Honeybadger.notify(error));
              }}
            />
          </PaymentFormContainer>
        )}
        <ConfirmDetails
          clinic={clinic}
          appointment={appointment}
          product={product}
          timeSlot={timeSlot}
          loading={loading}
          paymentTermsChecked={paymentTermsChecked}
          validPromo={validPromo}
          hasEmployer={hasEmployer}
          onPaymentTermsChecked={onPaymentTermsChecked}
          onValidPromoChange={onValidPromoChange}
        />
      </form>
    </PaymentFormWrapper>
  );
};

export default injectStripe(PaymentForm);

const PaymentFormContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

const CardElementContainer = styled.div`
  flex: 1 1 100%;
  margin-right: 10px;
  margin-bottom: -3px;
  padding-bottom: 1px;
  border-bottom: solid 1px;

  &#zip-code {
    width: calc(50% - 10px);
    height: 30px;
    margin-top: 12px;
    margin-bottom: 30px;
    margin-right: 0;
    padding: 0;
    border: none;
    flex: 0 0 auto;
    text-align: left;
    justify-content: flex-start;

    & > * {
      height: auto;
      padding: 0;
    }

    & input {
      font-family: serif;
      height: 35px;
      padding-top: 3px;
      padding-bottom: 3px;
    }

    &.complete.error input:focus,
    &.error:not(.complete) input:not(:focus) {
      color: #eb1c26;
    }
  }

  @media ${({ theme }) => theme.queries.desktop} {
    flex-basis: auto;
  }
`;

const SmallMediaCardElementsContainer = styled.div`
  width: 100%;
  margin-top: 15px;
  display: flex;
  flex-direction: row;

  & ${CardElementContainer} {
    flex: 1 1 auto;
  }
`;

const PromoCode = styled(PromoCodeBase)`
  width: 100%;
  flex: 0 0 auto;
  margin-top: 15px;
  text-align: left;
  justify-content: flex-start;
`;

const LoaderContainer = styled.div`
  margin-top: 50px;
`;
